import type { FC } from 'react';
import React, { useContext } from 'react';
import { Subscribe } from 'unstated';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
import { FULL_PAGE_EDITOR_TOOLBAR_HEIGHT } from '@atlaskit/editor-shared-styles/constants';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { BannerContext } from '@confluence/banners/entry-points/BannerContext.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { useGeneralWebPanelHeight } from '@confluence/editor-features/entry-points/useGeneralWebPanelHeight.ts';

export type AnchorExtensionHandlerProps = ExtensionHandlerProps & {
	isSSR: boolean;
};

const anchorWrapperStyles = xcss({
	position: 'relative',
	display: 'inline-block',
	height: '1em',
	width: '0',
});

const anchorStyles = xcss({
	position: 'absolute',
});

const getAnchorName = (node: any) => {
	const rawValue = node?.parameters?.macroParams?.['']?.value;
	if (typeof rawValue !== 'string') return null;
	return rawValue.replaceAll(/[%"'\s]/g, '');
};

const getLegacyAnchorName = (node: any) => {
	return node?.parameters?.macroParams?.['legacyAnchorId']?.value;
};

export const AnchorExtensionHandler: FC<AnchorExtensionHandlerProps> = ({ node, isSSR }) => {
	const isHidden = Boolean(node?.parameters?.macroParams?.hidden?.value);
	const anchorName = getAnchorName(node);
	const legacyAnchorName = getLegacyAnchorName(node);
	const { includeTopNav } = useContext(BannerContext);
	const panelHeight = useGeneralWebPanelHeight();

	// Note that we do not render `name` attribute on SSR stage for the following reason:
	// If `name` attribute would be rendered in SSR and there is a corresponding hash
	// in the browser url address field, the viewport will jump to an anchor as soon as HTML
	// is delivered to a client. However, right after that SPA rendering is happening which
	// involves replacing SSR-rendered DOM with SPA-rendered DOM. And this will cause the
	// viewport to jump back to the start of the page.
	const anchorAttr = isSSR ? {} : { name: anchorName };
	const legacyAnchorAttr = isSSR ? {} : { name: legacyAnchorName };

	return (
		<>
			{!isHidden ? (
				<Box as="span" xcss={anchorWrapperStyles}>
					<Subscribe to={[BannerStateContainer]}>
						{(bannerState: BannerStateContainer) => {
							const navAndExtensionHeight = bannerState.getTotalHeight(includeTopNav) + panelHeight;
							const toolbarHeight = FULL_PAGE_EDITOR_TOOLBAR_HEIGHT();

							return (
								<>
									{!!anchorName && (
										// oxlint-disable-next-line @atlassian/a11y/anchor-has-content
										<Box
											as="a"
											data-group="deeplink"
											testId="anchor-extension-handler-anchor"
											xcss={anchorStyles}
											style={{ bottom: `calc(${navAndExtensionHeight}px + ${toolbarHeight})` }}
											{...(anchorAttr as any)}
										/>
									)}
									{!!legacyAnchorName && (
										// oxlint-disable-next-line @atlassian/a11y/anchor-has-content
										<Box
											as="a"
											data-group="deeplink"
											testId="anchor-extension-handler-anchor-legacy"
											xcss={anchorStyles}
											style={{ bottom: `calc(${navAndExtensionHeight}px + ${toolbarHeight})` }}
											{...(legacyAnchorAttr as any)}
										/>
									)}
								</>
							);
						}}
					</Subscribe>
				</Box>
			) : null}
		</>
	);
};
